import React from 'react';
import type { NotificationRow } from '../../api';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { TruncatedText } from 'design-system/src/components/TruncatedText/TruncatedText';
import { formatStandardDate } from 'shared-utils/utils/formatDate';
import { SearchHighlightText } from '../SearchHighlightText/SearchHighlightText';
import { PreviewDateWithStatus } from '../Table';

interface NotificationPreviewItemProps {
  notification: NotificationRow;
  searchTerm: string;
}

/**
 * Preview item component for notification search results.
 * Highlights fields that match the search term (drug, patient name, DOB).
 */
export const NotificationPreviewItem: React.FC<NotificationPreviewItemProps> = ({ notification, searchTerm }) => {
  const formattedDob = formatStandardDate(notification.patient_dob);
  const isClosed = notification.status === 'completed';

  return (
    <div className="flex items-start justify-between gap-2">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-baseline gap-1">
          <Typography
            styledAs="bodySmallDMSans"
            renderedAs="div"
            weight={600}
            color="var(--grayscale-900)"
            className="max-w-[250px] shrink-0"
          >
            <TruncatedText lines={1} tooltipPlacement="bottom">
              {notification.task_type}
            </TruncatedText>
          </Typography>
          <Typography styledAs="bodySmallDMSans" renderedAs="span" color="var(--grayscale-600)" aria-hidden>
            |
          </Typography>
          <Typography
            styledAs="bodySmallDMSans"
            renderedAs="div"
            color="var(--grayscale-600)"
            className="min-w-0 flex-1"
          >
            <SearchHighlightText
              searchTerm={searchTerm}
              value={notification.drug_description}
              fieldType="text"
              truncate
            />
          </Typography>
        </div>
        <Typography styledAs="bodyExtraSmallDMSans" renderedAs="div" color="var(--grayscale-900)">
          <TruncatedText lines={1} tooltipPlacement="bottom">
            {notification.extra_context}
          </TruncatedText>
        </Typography>
        <div className="flex items-baseline gap-0.5">
          <Typography
            styledAs="bodyExtraSmallDMSans"
            renderedAs="div"
            weight={500}
            color="var(--grayscale-900)"
            className="max-w-[200px] shrink-0"
          >
            <SearchHighlightText searchTerm={searchTerm} value={notification.patient_name} fieldType="name" truncate />
          </Typography>
          {notification.patient_dob && (
            <Typography styledAs="bodyExtraSmallDMSans" renderedAs="span" color="var(--grayscale-600)">
              (DOB: <SearchHighlightText searchTerm={searchTerm} value={formattedDob} fieldType="date" />)
            </Typography>
          )}
        </div>
      </div>

      <PreviewDateWithStatus date={notification.created_at} statusLabel={isClosed ? 'Closed' : undefined} />
    </div>
  );
};

export default NotificationPreviewItem;
